import _map from 'lodash/map';
import _mapValues from 'lodash/mapValues';
import {
  AiProductSelectionConditionSignal,
  CampaignIssues,
  CampaignTag,
  RemoteCampaignSignal,
  RemoteCampaignStepsInformation,
  ServerSlimCampaign,
  SlimCampaign,
  SocialCampaignStatuses,
} from './campaigns.types';
import {PostApiOptions, PostDataObj} from '../api/apiTypes';
import {
  toClientCampaignStepsInformation,
  toClientConditions,
  toLocalCampaignSignal,
  toLocalSlimCampaign,
} from './campaigns.dataConverters';
import apiV2Service from '../api/api-v2.service';
import taxonomyApi from '../taxonomy/taxonomy.api';
import {toLocalSignal} from '../campaignTrackers/campaignTrackers.dataConverters';
import addRandomKeyToObjectArray from 'common/src/utils/arrayUtils/addRandomKeyToObjectArray';
import {
  CategorizedSignal,
  ServerCategorizedSignal,
} from '../compoundConditions/compoundConditions.types';
import {toClientDashboard} from '../dashboards/dashboards.dataConverters';
import {Dashboard, RemoteDashboard} from '../dashboards/dashboards.types';
import {EntityToRename} from 'common/src/modules/shared/copilot/copilot.types';
import {AIConditionSignal} from './copilot/agents/campaignSetupAgent/standaloneAgents/promptToCardAgentV2/promptToCardAgentV2.types';

const v2BasePath = 'Campaign';

async function deleteCampaign(campaignId: number) {
  return apiV2Service.executeDeleteRequest<boolean>(`${v2BasePath}/${campaignId}`);
}

async function duplicateCampaign(campaignId: number, includeAdGroups = false) {
  return apiV2Service.executePostRequest(`${v2BasePath}/${campaignId}/Duplicate`, {
    includeAdGroups,
  });
}

interface slimCampaignsRequestFilters {
  type?: string;
  status?: string;
  ids?: number[];
}

async function getSlimCampaigns(
  requestFilters?: slimCampaignsRequestFilters,
): Promise<SlimCampaign[]> {
  const {data} = await apiV2Service.executeGetRequest<ServerSlimCampaign[]>(`${v2BasePath}/List`, {
    ...(requestFilters?.type && {type: requestFilters.type}),
    ...(requestFilters?.status && {status: requestFilters.status}),
    ...(requestFilters?.ids && {ids: requestFilters.ids}),
  });

  return _map(data, toLocalSlimCampaign);
}

async function add(postDataObj: PostDataObj, postApiOptions: PostApiOptions = {}): Promise<number> {
  return apiV2Service
    .executePutRequest<number>(v2BasePath, {}, postDataObj, postApiOptions)
    .then((res) => res.data);
}

function getSignals({
  campaignId,
  versionId,
  feedId,
}: {
  campaignId: number;
  versionId: number;
  feedId: number;
}) {
  return apiV2Service
    .executeGetRequest<
      RemoteCampaignSignal[]
    >(`${v2BasePath}/${campaignId}/version/${versionId}/feed/${feedId}/conditions`)
    .then(({data}) => data.map(toLocalCampaignSignal));
}

function getContentMatchingSignals({
  campaignId,
  versionId,
  previousNodeId,
  previousOutputId,
  existingNodeId,
}: {
  campaignId: number;
  versionId: number;
  existingNodeId?: number;
  previousNodeId: number;
  previousOutputId: number;
}) {
  return apiV2Service
    .executeGetRequest<RemoteCampaignSignal[]>(
      `${v2BasePath}/${campaignId}/version/${versionId}/matchingStrategySignals`,
      {
        ...(existingNodeId && {existingNodeId}),
        ...(!existingNodeId && !!previousNodeId && {previousNodeId, previousOutputId}),
      },
    )
    .then(({data}) => data.map(toLocalCampaignSignal));
}

async function getFullConditionCategories(
  campaignId: number,
  versionId: number,
): Promise<CategorizedSignal[]> {
  const [taxonomy, serverSignals] = await Promise.all([
    taxonomyApi.getMapping(),
    apiV2Service
      .executeGetRequest<ServerCategorizedSignal[]>(`${v2BasePath}/GetFullConditionCategories`, {
        campaignId,
        versionId,
      })
      .then(({data}) =>
        data.map((category) => ({...category, categories: category.categoryId ?? []})),
      ),
  ]);

  const signals = serverSignals.map(toLocalSignal);

  return toClientConditions(signals, taxonomy);
}

async function getCampaignStatuses({
  campaignId,
  versionId,
}: {
  campaignId: number;
  versionId: number;
}) {
  const {data} = await apiV2Service.executeGetRequest<SocialCampaignStatuses>(
    `${v2BasePath}/${campaignId}/version/${versionId}/GetCampaignStatuses`,
  );

  return data;
}

async function deactivateCampaign(campaignId: number) {
  return apiV2Service.executePostRequest<void>(`${v2BasePath}/${campaignId}/Deactivate`);
}

async function activateCampaign(campaignId: number) {
  return apiV2Service.executePostRequest<void>(`${v2BasePath}/${campaignId}/Activate`);
}

async function getCampaignIssues(campaignId: number, versionId: number) {
  return apiV2Service
    .executeGetRequest<CampaignIssues>(
      `${v2BasePath}/${campaignId}/version/${versionId}/GetIssues/issues`,
    )
    .then(({data}) => {
      const {
        campaignLogicIssues,
        thirdPartyTrackersIssues,
        campaignIntegrationIssues,
        placementIssues,
      } = data;
      const nodeIssues = addRandomKeyToObjectArray(campaignLogicIssues);
      const trackersIssues = addRandomKeyToObjectArray(thirdPartyTrackersIssues);
      const integrationIssues = addRandomKeyToObjectArray(campaignIntegrationIssues);
      const placementsIssues = addRandomKeyToObjectArray(placementIssues || []);

      return {
        campaignLogicIssues: nodeIssues.map((issue) => ({...issue, issueKey: issue.key})),
        thirdPartyTrackersIssues: trackersIssues.map((issue) => ({...issue, issueKey: issue.key})),
        campaignIntegrationIssues: integrationIssues.map((issue) => ({
          ...issue,
          issueKey: issue.key,
        })),
        placementIssues: placementsIssues.map((issue) => ({...issue, issueKey: issue.key})),
      };
    });
}

// Campaign Dashboard

async function getCampaignDashboards(campaignId: number): Promise<Dashboard[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard[]>(
    `${v2BasePath}/${campaignId}/Dashboards`,
  );

  return _map(data, toClientDashboard);
}

async function getCampaignDefaultDashboard(campaignId: number): Promise<Dashboard> {
  const {data} = await apiV2Service.executeGetRequest<RemoteDashboard>(
    `${v2BasePath}/${campaignId}/DefaultDashboard`,
  );

  return toClientDashboard(data);
}

async function setAsDefaultCampaignDashboard(
  dashboardId: number,
  campaignId: number,
): Promise<number> {
  const {data} = await apiV2Service.executePutRequest<number>(
    `${v2BasePath}/${campaignId}/DefaultDashboard/${dashboardId}`,
  );

  return data;
}

async function createCampaignDashboard(campaignId: number): Promise<number> {
  const {data} = await apiV2Service.executePostRequest<number>(
    `${v2BasePath}/${campaignId}/CreateDashboard`,
  );

  return data;
}

function monitorEmptyIssues(campaignId: number, versionId: number) {
  apiV2Service.executePostRequest<void>(
    `${v2BasePath}/${campaignId}/version/${versionId}/monitorNoIssuesAfterPatch`,
    {campaignId, versionId},
  );
}

async function removeCampaignDashboard(campaignId: number, dashboardId: number) {
  const {data} = await apiV2Service.executeDeleteRequest<number>(
    `${v2BasePath}/${campaignId}/${dashboardId}`,
  );

  return data;
}

async function renameCampaigns(campaigns: EntityToRename[]) {
  return Promise.all(
    campaigns.map((campaign) =>
      apiV2Service.executePatchRequest<void>(
        `${v2BasePath}/${campaign.id}/basicData`,
        {},
        {
          name: campaign.newName,
        },
      ),
    ),
  );
}

async function assignTags(campaignId: number, tags: CampaignTag[]) {
  return apiV2Service.executePatchRequest<void>(
    `${v2BasePath}/${campaignId}/basicData`,
    {},
    {
      tag1: tags[0],
      tag2: tags[1],
      tag3: tags[2],
    },
  );
}

async function getSignalsByNameForConditionCard(
  signalName: string,
  campaignId: number,
  versionId: number,
): Promise<AIConditionSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<AIConditionSignal[]>(
    `${v2BasePath}/${campaignId}/version/${versionId}/searchConditionCardSignals`,
    {
      signalName,
      campaignId,
      versionId,
    },
  );

  return data;
}

async function getSignalsByNameForContentCard({
  signalName,
  campaignId,
  versionId,
}: {
  signalName: string;
  campaignId: number;
  versionId: number;
}): Promise<AiProductSelectionConditionSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<AiProductSelectionConditionSignal[]>(
    `${v2BasePath}/${campaignId}/version/${versionId}/searchContentMatchingSignals`,
    {
      signalName,
      campaignId,
      versionId,
    },
  );

  return data;
}

async function getSignalsByNameForProductCard({
  signalName,
  versionId,
  productType,
  campaignId,
}: {
  signalName: string;
  versionId: number;
  productType: number;
  campaignId: number;
}): Promise<AiProductSelectionConditionSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<AiProductSelectionConditionSignal[]>(
    `${v2BasePath}/${campaignId}/version/${versionId}/searchProductMatchingSignals`,
    {
      signalName,
      productType,
    },
  );

  return data;
}

async function fetchCollaborationBadgesData(campaignIds: number[]) {
  const {data} = await apiV2Service.executePostRequest<
    Record<number, RemoteCampaignStepsInformation>
  >(`${v2BasePath}/CollaborationBadges`, {}, {ids: campaignIds});

  return _mapValues(data, toClientCampaignStepsInformation);
}

async function getSportTeamNameSignals({
  campaignId,
  versionId,
}: {
  campaignId: number;
  versionId: number;
}): Promise<RemoteCampaignSignal[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteCampaignSignal[]>(
    `Signals/${campaignId}/version/${versionId}/sportTeamNameSignals`,
  );

  return data;
}

const campaignsApi = {
  add,
  deleteCampaign,
  duplicateCampaign,
  getSlimCampaigns,
  getSignals,
  getContentMatchingSignals,
  getFullConditionCategories,
  activateCampaign,
  deactivateCampaign,
  getCampaignStatuses,
  getCampaignIssues,
  // Campaign Dashboard
  getCampaignDefaultDashboard,
  getCampaignDashboards,
  setAsDefaultCampaignDashboard,
  createCampaignDashboard,
  monitorEmptyIssues,
  removeCampaignDashboard,
  renameCampaigns,
  assignTags,
  getSignalsByNameForConditionCard,
  getSignalsByNameForContentCard,
  getSignalsByNameForProductCard,
  fetchCollaborationBadgesData,
  getSportTeamNameSignals,
};

export default campaignsApi;
